/**
 * Desktop Push Notification Service
 * Handles browser notification permissions and sending notifications
 */

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.supported = 'Notification' in window;
    this.initialized = false;
  }

  /**
   * Check if browser supports notifications
   */
  isSupported() {
    return this.supported;
  }

  /**
   * Get current permission status
   */
  getPermission() {
    if (!this.supported) return 'denied';
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission() {
    if (!this.supported) {
      console.warn('Browser does not support notifications');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Check if we can send notifications
   */
  canNotify() {
    return this.supported && Notification.permission === 'granted';
  }

  /**
   * Send a desktop notification
   * @param {string} title - Notification title
   * @param {object} options - Notification options
   * @returns {Notification|null}
   */
  send(title, options = {}) {
    if (!this.canNotify()) {
      console.warn('Cannot send notification - permission not granted');
      return null;
    }

    const defaultOptions = {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'lma-notification',
      requireInteraction: false,
      silent: false,
    };

    const notification = new Notification(title, {
      ...defaultOptions,
      ...options,
    });

    // Auto-close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);

    return notification;
  }

  /**
   * Send a new lead notification
   * @param {object} lead - Lead data
   */
  sendNewLeadNotification(lead) {
    const title = `New Lead: ${lead.title || lead.name || 'Unknown'}`;
    const body = lead.message || `${lead.email || ''} from ${lead.source || 'Website'}`;

    const notification = this.send(title, {
      body,
      tag: `lead-${lead.id || Date.now()}`,
      data: { type: 'new_lead', dealId: lead.deal_id },
      requireInteraction: true,
    });

    if (notification) {
      notification.onclick = () => {
        window.focus();
        if (lead.deal_id) {
          // Navigate to the deal
          window.location.hash = `deal=${lead.deal_id}`;
        }
        notification.close();
      };
    }

    return notification;
  }

  /**
   * Send a task reminder notification
   * @param {object} task - Task data
   */
  sendTaskReminderNotification(task) {
    const title = `Task Due: ${task.title}`;
    const body = task.description || `Due: ${task.due_date}`;

    return this.send(title, {
      body,
      tag: `task-${task.id}`,
      data: { type: 'task_reminder', taskId: task.id },
      requireInteraction: true,
    });
  }

  /**
   * Send a general notification
   * @param {object} notificationData - Notification data from API
   */
  sendFromApiNotification(notificationData) {
    const title = notificationData.title || 'Notification';
    const body = notificationData.message || '';

    const notification = this.send(title, {
      body,
      tag: `api-${notificationData.id || Date.now()}`,
      data: { 
        type: notificationData.type,
        dealId: notificationData.deal_id,
        link: notificationData.link,
      },
    });

    if (notification && notificationData.deal_id) {
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }

    return notification;
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
export { NotificationService };
